import React, {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef
} from "react";
import html2canvas from "html2canvas";
import toast from "react-hot-toast";
import PaySlipBulkPdf from "../paySlipBulkPdf";
import jsPDF from "jspdf";
import { profileAttachmentToDrawinBox } from "../../../helper/api/api";

const BulkPayslipUploader = ({ user, listDownload, monthName, processPeriod, fileName, onUploadComplete, formData }, ref) => {
  const payslipRef = useRef();

  useImperativeHandle(
    ref,
    () => ({
      uploadPayslips: async () => {
        return await uploadPayslips();
      },
    }),
    [listDownload]
  );

  const uploadPayslips = async () => {
    const container = payslipRef.current;
    const children = Array.from((container && container.children) || []);
    const inProgressToastId = toast.loading("Uploading payslips...");

    const failures = [];
    const successes = [];

    if (!listDownload || listDownload.length === 0) {
      toast.dismiss(inProgressToastId);
      toast.error("No payslips to upload.");
      return;
    }

    for (let i = 0; i < listDownload.length; i++) {
      const empRows = listDownload[i];
      const emp = empRows?.[0];

      if (!emp) {
        failures.push({ employeeNumber: `index-${i}`, reason: "No employee data" });
        continue;
      }

      const employeeNumber = emp.EmployeeNumber;

      // find matching DOM node
      const payslipDomNode = children.find(
        (node) => node && node.dataset.employeeId === String(employeeNumber)
      );

      if (!payslipDomNode) {
        failures.push({ employeeNumber, reason: "Payslip DOM not found" });
        continue;
      }

      try {
        // 1. Render HTML to Canvas at a higher scale for better clarity.
        //    Increasing `scale` renders more pixels (sharper text), then we compress
        //    to JPEG to keep overall size reasonable. Try values 1.8-2.5 for best results.
        const canvas = await html2canvas(payslipDomNode, {
          scale: 2.0,
          useCORS: true,
        });

        // 2. Create PDF from canvas using jsPDF
        //    Use JPEG with higher quality to preserve clarity while compressing.
        //    If size is still too large, reduce `quality` or reduce `scale` above.
        const imgData = canvas.toDataURL("image/jpeg", 0.85); // quality 0.85
        const imgWidth = 210; // A4 width in mm adjusted for margins
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });

        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
        const pdfHeight = pdf.internal.pageSize.getHeight();
        const timestamp = new Date().toLocaleString();
        pdf.setFontSize(8); 
        pdf.setTextColor(100);
        pdf.text(`Generated on: ${timestamp} - Printed by: ${user[0]?.FullName || user[0]?.UserName || ''}`, 10, pdfHeight - 10);
        // 3. Convert to base64
        const pdfBase64 = pdf.output("dataurlstring").split(",")[1];

        // 4. Prepare payload
        const payload = {
          api_key: process.env.REACT_APP_PAYSLIP_APIKEY,
          employee_no: emp.EmployeeNumber,
          section: "hr_letters",
          section_attribute: "Payslips",
          attachment: pdfBase64,
          attachment_file_name: `${emp.EmployeeNumber}_${fileName}`,
          attachment_file_extension: "pdf",
          OU: user[0]?.OU || "",
          Role: user[0]?.Role || "",
          User: user[0]?.UserName || "",
          ProcessPeriod: processPeriod || "",
        };

        // 5. API Upload
        console.log(`📤 Uploading payslip for ${emp.EmployeeNumber}...`);
        let res = await profileAttachmentToDrawinBox(payload);
        if (!res || res.status < 200 || res.status >= 300) {
          failures.push({ employeeNumber, reason: "HTTP error " + res.status });
          continue;
        }

        successes.push(employeeNumber);
      } catch (err) {
        failures.push({
          employeeNumber: emp.EmployeeNumber,
          reason: err.response.data.message,
        });
      }
    }

    toast.dismiss(inProgressToastId);

    if (failures.length === 0) {
      toast.success(`All payslips uploaded successfully (${successes.length})`);
    } else {
      toast.error(
        `Uploaded: ${successes.length}, Failed: ${failures.length}.${failures[0].reason ? ` ${failures[0].reason}` : "Please contact admin"}.`
      );
      console.log("Failures:", failures);
    }
    if (onUploadComplete) {
      onUploadComplete();
    }
  };

  return (
    <div
      ref={payslipRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        opacity: 0,
        pointerEvents: "none",
        width: "50%",
        zIndex: -1,
      }}
    >
      {listDownload?.map((empData, idx) => (
        <div key={idx} data-employee-id={empData?.[0]?.EmployeeNumber}>
          <PaySlipBulkPdf data={empData || []} month={monthName} formData={formData} />
        </div>
      ))}
    </div>
  );
};

export default forwardRef(BulkPayslipUploader);
